import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import * as Application from 'expo-application';
import Constants from 'expo-constants';
import * as Updates from 'expo-updates';

const manifest = Updates.manifest;

const metadata = 'metadata' in manifest ? manifest.metadata : undefined;

const extra = 'extra' in manifest ? manifest.extra : undefined;

const updateGroup = metadata && 'updateGroup' in metadata ? metadata.updateGroup : undefined;

type Detail = {
  key: string;
  value?: string | boolean | number | null;
  label: string;
  debug: boolean;
  emphasize?: boolean;
  display?: string | boolean | number | null;
  copyLines?: string[];
};

/**
 * Canonical field order for the analytics build context. Exported so the two
 * Segment transports can iterate a typed key list rather than casting the
 * result of `Object.entries`.
 */
export const BUILD_CONTEXT_KEYS = [
  'app_version',
  'native_build_version',
  'git_commit_sha',
  'ota_update_id',
  'ota_update_group_id',
  'is_embedded_launch',
  'web_deployment_id',
] as const;

export type BuildContextKey = (typeof BUILD_CONTEXT_KEYS)[number];

export type BuildContext = Partial<Record<BuildContextKey, string | boolean | null>>;

/** Stands in for the update ID when the running bundle came from the binary. */
const EMBEDDED_UPDATE_ID = 'embedded';

export function getBuildInformation() {
  const buildDetails: Detail[] = [
    {
      key: 'version',
      label: 'Version',
      value: isWeb
        ? Constants.expoConfig?.version || process.env.EXPO_PUBLIC_PACKAGE_VERSION
        : Application.nativeApplicationVersion,
      debug: false,
    },
  ];

  if (!isWeb && Application.nativeBuildVersion) {
    buildDetails.push({
      key: 'build',
      label: 'Build',
      value: Application.nativeBuildVersion,
      debug: false,
    });
  }

  if (process.env.EXPO_PUBLIC_GIT_COMMIT_SHA) {
    buildDetails.push({
      key: 'git-commit-sha',
      value: process.env.EXPO_PUBLIC_GIT_COMMIT_SHA,
      display: process.env.EXPO_PUBLIC_GIT_COMMIT_SHA?.substring?.(0, 7),
      label: 'Commit',
      debug: false,
      emphasize: true,
    });
  }

  if (isWeb) {
    buildDetails.push({
      key: 'web-deploy-id',
      value: process.env.EXPO_PUBLIC_DEPLOYMENT_ID,
      display: process.env.EXPO_PUBLIC_DEPLOYMENT_ID?.substring?.(0, 7),
      label: 'Deployment',
      debug: false,
    });
  } else {
    if (Updates.updateId) {
      if (typeof updateGroup === 'string') {
        const updateGroupShort = updateGroup.substring(0, 7);

        const updateIdShort = Updates.updateId?.substring?.(0, 7);

        buildDetails.push({
          key: 'expo-update-group-id',
          value: updateGroup,
          display: `${updateGroupShort} (${updateIdShort})`,
          label: 'Update',
          debug: false,
          emphasize: true,
          copyLines: [`Update Group: ${updateGroup}`, `Update ID: ${Updates.updateId}`],
        });
      }

      const owner = extra?.expoClient?.owner ?? '[account]';

      const slug = extra?.expoClient?.slug ?? '[project]';

      if (metadata && 'updateGroup' in metadata) {
        buildDetails.push({
          label: 'Update Debug URL',
          key: 'expo-update-debug-url',
          value: `https://expo.dev/accounts/${owner}/projects/${slug}/updates/${metadata.updateGroup}`,
          debug: true,
        });
      }
    } else if (Updates.isEmbeddedLaunch) {
      buildDetails.push({
        label: 'Update Debug URL',
        key: 'expo-update-debug-url',
        value: 'not applicable for embedded updates',
        debug: true,
      });
    }
  }

  if (process.env.EXPO_PUBLIC_GIT_COMMIT_SHA) {
    buildDetails.push({
      label: 'Github Debug URL',
      key: 'github-debug-url',
      // The CI workflows set the repository the build came from; EAS native
      // builds do not, so they fall back to the monorepo.
      value: `https://github.com/${process.env.EXPO_PUBLIC_GIT_REPOSITORY || 'primuminc/primum'}/commit/${process.env.EXPO_PUBLIC_GIT_COMMIT_SHA}`,
      debug: true,
    });
  }

  const clarityProjectId = isWeb
    ? process.env.EXPO_PUBLIC_CLARITY_PROJECT_ID
    : process.env.EXPO_PUBLIC_CLARITY_MOBILE_PROJECT_ID;

  if (clarityProjectId) {
    buildDetails.push({
      key: 'clarity-project-id',
      label: 'Clarity Project',
      value: clarityProjectId,
      debug: true,
    });
  }

  return buildDetails;
}

export function getAppVersion(): string | undefined {
  if (isWeb) {
    return Constants.expoConfig?.version || process.env.EXPO_PUBLIC_PACKAGE_VERSION;
  }

  return Application.nativeApplicationVersion || undefined;
}

/**
 * Build identity shaped for analytics destinations: flat, snake_case, and
 * explicitly valued. Kept separate from the settings-sheet build details, whose
 * kebab-case keys and label/display/debug fields serve a human reader and whose
 * update entry carries the update *group* while burying the update ID in copy
 * text.
 *
 * Every key present for the running platform holds a real value or an explicit
 * `null` — never `undefined`. Segment strips undefined keys, and a stripped key
 * does not read as "no data" downstream: a user profile keeps whatever it held
 * before, so an OTA rollback would go on reporting the update it rolled back
 * from. A field that was silently absent from user profiles for this reason is
 * what surfaced this.
 *
 * The OTA fields are deliberately absent on web rather than null. Native and web
 * share one user profile, so a null written from web would clobber the update ID
 * the native app had recorded for the same person.
 */
export function getBuildContext(): BuildContext {
  const gitCommitSha = process.env.EXPO_PUBLIC_GIT_COMMIT_SHA || null;

  if (isWeb) {
    return {
      app_version: getAppVersion() || null,
      git_commit_sha: gitCommitSha,
      web_deployment_id: process.env.EXPO_PUBLIC_DEPLOYMENT_ID || null,
    };
  }

  return {
    app_version: getAppVersion() || null,
    native_build_version: Application.nativeBuildVersion || null,
    git_commit_sha: gitCommitSha,
    ota_update_id: Updates.updateId || (Updates.isEmbeddedLaunch ? EMBEDDED_UPDATE_ID : null),
    ota_update_group_id: typeof updateGroup === 'string' ? updateGroup : null,
    is_embedded_launch: Updates.isEmbeddedLaunch,
  };
}
